import { createSlice } from '@reduxjs/toolkit';

// Type definitions
interface Video {
  id?: string;
  videoId?: string;
  status?: string;
  loadingStatus?: string;
  uploadStatus?: string;
  thumbnail?: string;
  [key: string]: any;
}

interface SelectedField {
  field: string;
  selectedOptions: any[];
}

interface Filters {
  thirdpartyClaimsCount?: any;
  uploadStatus?: any;
  videoKind?: any;
  monetize?: any;
  privacyStatus?: any;
  uploadedAt?: any;
  publishedAt?: any;
  uploadedDateStart?: any;
  uploadedDateEnd?: any;
  publishedDateStart?: any;
  publishedDateEnd?: any;
  creationTime?: any;
  publishedTime?: any;
}

interface VideoState {
  status: string;
  uploadingVideos: any[];
  videos: Video[];
  error: any;
  columns: any[];
  filters: Filters;
  fields: any[];
  selectedFields: SelectedField[];
  selectedColumns: string[];
  fileList: any[];
  cancellationFlags: Record<string, boolean>;
}

// Initial state
const initialState: VideoState = {
  status: 'idle', // Genel durum
  uploadingVideos: [], // Yükleme durumlarını takip eder
  videos: [], // Yüklenen videolar listesi
  error: null, // Hata mesajı
  columns: [], // Tablo sütunları
  filters: {
    thirdpartyClaimsCount: undefined,
    uploadStatus: undefined,
    videoKind: undefined,
    monetize: undefined,
    privacyStatus: undefined,
    uploadedAt: undefined,
    publishedAt: undefined,
    uploadedDateStart: undefined,
    uploadedDateEnd: undefined,
    publishedDateStart: undefined,
    publishedDateEnd: undefined,
    creationTime: undefined,
    publishedTime: undefined,
  },
  fields: [],
  selectedFields: [],
  selectedColumns: (() => {
    const stored = localStorage.getItem('video_selected_columns');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // JSON parse hatası durumunda default değerleri döndür
      }
    }
    return [
      'video',
      'fileName',
      'description',
      'privacyStatus',
      'monetize',
      'clientUploadStatus',
      'videoKind',
      'publisher',
      'uploader',
      'uploadedAt',
      'publishedAt',
      'plannedPublishDate',
      'product',
      'viewCount',
      'thirdpartyClaimsCount',
      'isPolicyStatus',
      'policy',
      'operation',
    ];
  })(),
  fileList: [],
  cancellationFlags: {},
};

const videoSlice = createSlice({
  name: 'video',
  initialState,
  reducers: {
    addVideo: (state, action) => {
      // Videoları en üste ekle
      state.videos.unshift(action.payload);
    },
    updateVideoStatus: (state, action) => {
      const { id, status, loadingStatus, videoId, uploadStatus, thumbnail } = action.payload;
      void action;
      // İlk yükleme durumu için
      let video = state.videos.find((v) => v.id === id);

      // Yükleme tamamlandıktan sonraki durum için
      if (!video) {
        video = state.videos.find((v) => v.videoId === id);
      }

      if (video) {
        if (status) {
          video.status = status;
        }
        if (loadingStatus !== undefined) {
          video.loadingStatus = loadingStatus;
        }
        if (videoId) {
          video.videoId = videoId;
        }
        if (uploadStatus) {
          video.uploadStatus = uploadStatus;
        }
        if (thumbnail) {
          video.thumbnail = thumbnail;
        }
      }
    },

    setSelectedColumns: (state, action) => {
      state.selectedColumns = action.payload;
    },
    setSelectedFields: (state, action) => {
      state.selectedFields = action.payload;
    },
    setFields: (state, action) => {
      state.fields = action.payload;
    },
    setColumns: (state, action) => {
      state.columns = action.payload;
    },
    updateSelectedFields: (state, action) => {
      const { field, selectedOptions } = action.payload;
      const existingField = state.selectedFields.find((f) => f.field === field);

      if (existingField) {
        existingField.selectedOptions = selectedOptions;
      } else {
        state.selectedFields.push({ field, selectedOptions });
      }
    },
    updateFields: (state, action) => {
      state.fields = action.payload;
    },
    updateFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: (state) => {
      state.filters = {
        thirdpartyClaimsCount: undefined,
        uploadStatus: undefined,
        videoKind: undefined,
        monetize: undefined,
        privacyStatus: undefined,
        uploadedAt: undefined,
        publishedAt: undefined,
        uploadedDateStart: undefined,
        uploadedDateEnd: undefined,
        publishedDateStart: undefined,
        publishedDateEnd: undefined,
        creationTime: undefined,
        publishedTime: undefined,
      };
    },
    setFileList: (state, action) => {
      state.fileList = action.payload;
    },
    updateFileList: (state, action) => {
      state.fileList = action.payload;
    },
    setCancellationFlag: (state, action) => {
      const { videoId, value } = action.payload;
      state.cancellationFlags[videoId] = value;
    },
    clearCancellationFlag: (state, action) => {
      const { videoId } = action.payload;
      delete state.cancellationFlags[videoId];
    },

    // Add a utility to clear all flags
    clearAllCancellationFlags: (state) => {
      state.cancellationFlags = {};
    },
  },
});

export const {
  updateFilters,
  resetFilters,
  updateFields,
  setFields,
  setSelectedFields,
  setSelectedColumns,
  updateSelectedFields,
  setColumns,
  addVideo,
  updateVideoStatus,
  setFileList,
  updateFileList,
  setCancellationFlag,
  clearCancellationFlag,
  clearAllCancellationFlags,
} = videoSlice.actions;

export default videoSlice.reducer;
